// src/components/student/StudentVideoPlayer.tsx
import React, { useState, useEffect, useRef } from "react";
import PlyrPlayer from "../PlyrPlayer";
import { useGetPlayUrlMutation } from "../../services/stream";

interface StudentVideoPlayerProps {
  courseId: string;
  moduleId: string;
  lessonId: string;
  lessonTitle?: string;
  className?: string;
  onError?: (error: string) => void;
  onLoad?: () => void;
  onVideoEnd?: () => void;
}

const StudentVideoPlayer: React.FC<StudentVideoPlayerProps> = ({
  courseId,
  moduleId,
  lessonId,
  lessonTitle,
  className,
  onError,
  onLoad,
  onVideoEnd,
}) => {
  const [getPlayUrl, { isLoading }] = useGetPlayUrlMutation();

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  console.log(tokenExpiry);

  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const playerMountedRef = useRef(true);

  // Fetch secure video URL from backend
  const fetchSecureVideoUrl = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        // when doing a fresh load (not a token refresh) clear old url so UI shows loading
        setVideoUrl(null);
      }
      setError(null);

      const response = await getPlayUrl({
        courseId,
        moduleId,
        lessonId,
      }).unwrap();

      if (!playerMountedRef.current) return; // Component unmounted

      setVideoUrl(response.signedUrl);
      setTokenExpiry(response.expires);
      setIsRefreshing(false);

      if (onLoad && !isRefresh) onLoad();

      // Calculate when to refresh token (30 seconds before expiry)
      const timeUntilExpiry = response.expiresIn * 1000; // Convert to milliseconds
      const refreshTime = timeUntilExpiry - 30 * 1000; // 30 seconds before expiry

      // Clear any existing refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Set up token refresh before expiry
      if (refreshTime > 0) {
        console.log(
          `[Video Player] Token will refresh in ${Math.floor(
            refreshTime / 1000,
          )} seconds`,
        );
        refreshTimeoutRef.current = setTimeout(() => {
          if (playerMountedRef.current) {
            console.log("[Video Player] Refreshing video token...");
            fetchSecureVideoUrl(true);
          }
        }, refreshTime);
      } else {
        // If refresh time is already passed, refresh immediately
        console.warn("[Video Player] Token already expired, refreshing now");
        setTimeout(() => {
          if (playerMountedRef.current) {
            fetchSecureVideoUrl(true);
          }
        }, 1000);
      }
    } catch (err: any) {
      console.error("[Video Player] Error fetching secure video URL:", err);

      if (!playerMountedRef.current) return;

      let errorMessage = "Failed to load video";

      // Handle specific error types
      if (err.status === 401) {
        errorMessage = "Please log in to watch this video";
      } else if (err.status === 403) {
        errorMessage = "You do not have access to this video";
      } else if (err.status === 404) {
        errorMessage = "Video not found";
      } else if (err.data?.error) {
        errorMessage = err.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setIsRefreshing(false);
      if (onError) onError(errorMessage);
    }
  };

  // Initial load and reload when lessonId / courseId / moduleId changes
  useEffect(() => {
    playerMountedRef.current = true;
    fetchSecureVideoUrl(false);

    // Cleanup
    return () => {
      playerMountedRef.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, moduleId, lessonId]);

  // Handle video ended event
  const handleVideoEnded = () => {
    console.log("[Video Player] Video ended");
    if (onVideoEnd) onVideoEnd();
  };

  if (isLoading && !videoUrl) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-900 rounded-lg ${
          className || ""
        }`}
        style={{ minHeight: 400 }}
      >
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading video...</p>
          <p className="text-sm text-gray-400 mt-2">Please wait</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-900 rounded-lg ${
          className || ""
        }`}
        style={{ minHeight: 400 }}
      >
        <div className="text-center text-white px-4 py-8">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-xl font-semibold mb-2">Unable to Load Video</h3>
          <p className="text-gray-400 mb-6 max-w-md">{error}</p>
          <button
            onClick={() => fetchSecureVideoUrl(false)}
            className="px-6 py-3 bg-teal-500 hover:bg-teal-600 rounded-lg transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!videoUrl) {
    return null;
  }

  return (
    <div className={className}>
      {/* Video Title */}
      {lessonTitle && (
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900">{lessonTitle}</h2>
        </div>
      )}

      {/* Video Player Container */}
      <div className="relative bg-black rounded-lg overflow-hidden">
        {/* Refreshing Indicator Overlay */}
        {isRefreshing && (
          <div className="absolute top-4 right-4 z-10 bg-black bg-opacity-70 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-2">
            <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
            <span>Refreshing token...</span>
          </div>
        )}

        {/* Video Player */}
        <div
          className="relative"
          onContextMenu={(e) => e.preventDefault()} // Disable right-click
        >
          {/* 
            IMPORTANT: give PlyrPlayer a key based on videoUrl so it remounts
            whenever the signed URL changes (e.g. when navigating lessons).
            This forces the player to re-initialize with the new source.
          */}
          <PlyrPlayer
            key={videoUrl}
            provider="html5"
            sources={[
              {
                src: videoUrl,
                type: "video/mp4",
              },
            ]}
            options={{
              controls: [
                "play-large",
                "restart",
                "rewind",
                "play",
                "fast-forward",
                "progress",
                "current-time",
                "duration",
                "mute",
                "volume",
                "settings",
                "pip",
                "fullscreen",
              ],
              settings: ["quality", "speed"],
              speed: {
                selected: 1,
                options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
              },
              quality: {
                default: 720,
                options: [1080, 720, 480, 360],
              },
              autoplay: false,
              hideControls: true,
              keyboard: { focused: true, global: false },
              tooltips: { controls: true, seek: true },
              seekTime: 10,
              resetOnEnd: false,
              listeners: {
                ended: handleVideoEnded,
              },
            }}
          />

          {/* User Watermark for Security */}
          <div
            className="absolute top-4 right-4 text-white text-xs opacity-20 pointer-events-none select-none z-10"
            style={{
              textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
              fontFamily: "monospace",
            }}
          >
            {localStorage.getItem("userEmail") || "Student"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentVideoPlayer;
